import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  ClipboardList,
  FileText,
  Mail,
  TriangleAlert,
} from 'lucide-react';
import {
  ACTION_PLAN_STATUS_LABEL,
  AUDIT_KIND_LABEL,
  AUDIT_STATUS_LABEL,
  CERT_STANDARD_LABEL,
  SUPPLIER_DOC_KIND_LABEL,
  type PendencyDto,
  type SupplierSelfDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { usePageLabel } from '../lib/navigation';
import { formatCnpj, formatCompetence, formatDate, formatPlace } from '../lib/format';
import { Alert, Badge, Card, EmptyState, Skeleton } from '../components/ui';

/**
 * `/minha-empresa` — a ficha vista pelo próprio fornecedor.
 *
 * Até aqui ele via a nota e não via **o que a compõe**: quais documentos estão
 * vencendo, quais RNCs estão abertas, que plano de ação foi aberto no nome dele.
 * A Qualidade tinha a ficha inteira; o fornecedor tinha um número.
 *
 * A tela **mostra e não edita**, com uma exceção: certificado, que se envia em
 * `/certificados`. O resto é registro da Weloze sobre a relação, e o caminho
 * para corrigir é falar com a Qualidade — não editar aqui e a Qualidade
 * descobrir depois.
 */
export function MyCompanyPage() {
  const { currentStore } = useAuth();
  const storeId = currentStore?.storeId;

  const [data, setData] = useState<SupplierSelfDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!storeId) return;
    setError(null);
    try {
      setData(await api.get<SupplierSelfDto>(`/stores/${storeId}/self`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar a sua ficha.');
    }
  }, [storeId]);

  useEffect(() => {
    void load();
  }, [load]);

  usePageLabel(data ? data.displayName : null);

  if (!storeId) {
    return (
      <EmptyState
        title="Nenhuma loja vinculada"
        description="Seu usuário ainda não tem loja liberada. Fale com a Qualidade da Weloze."
      />
    );
  }

  if (error) return <Alert>{error}</Alert>;

  if (!data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-96" />
        <Skeleton className="h-32" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const rncsAbertas = data.nonconformities.filter(
    (rnc) => rnc.status !== 'CLOSED' && rnc.status !== 'CANCELLED',
  );
  // Encerrado é `DONE` ou `CANCELLED`; `OVERDUE` continua aberto, e é o que
  // mais importa aparecer.
  const planosAbertos = data.actionPlans.filter(
    (plano) => plano.status !== 'DONE' && plano.status !== 'CANCELLED',
  );

  return (
    <div className="space-y-6">
      <header>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            {data.displayName}
          </h1>
          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-sm text-slate-700">
            {data.protheusRef}
          </span>
          {data.isPreferred && <Badge tone="brand">fornecedor preferencial</Badge>}
        </div>
        <p className="mt-1 text-sm text-slate-600">
          {data.legalName} · {formatCnpj(data.cnpj)} · {formatPlace(data.city, data.state)}
          {data.monitoredFrom && ` · avaliado desde ${formatCompetence(data.monitoredFrom)}`}
        </p>
      </header>

      <Pendencies pendencies={data.pendencies} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Bloco
          titulo="RNCs"
          icone={<TriangleAlert className="size-4" aria-hidden />}
          vazio="Nenhuma RNC emitida para a sua loja."
          acao={{ to: '/rncs', texto: 'Ver todas' }}
          itens={data.nonconformities.length}
        >
          <p className="mb-2 text-sm text-slate-600">
            {rncsAbertas.length === 0
              ? 'Nenhuma em tratativa.'
              : `${rncsAbertas.length} aguardando o seu 8D.`}
          </p>
          <ul className="divide-y divide-slate-100">
            {[...rncsAbertas, ...data.nonconformities.filter((r) => !rncsAbertas.includes(r))]
              .slice(0, 6)
              .map((rnc) => {
                const atrasadas = rnc.stages.filter((etapa) => etapa.overdue);
                return (
                  <li key={rnc.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                    <Link
                      to={`/rncs/${rnc.id}`}
                      className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-medium text-slate-800 underline-offset-4 hover:bg-slate-200 hover:underline"
                    >
                      {rnc.number}
                    </Link>
                    <span className="min-w-0 flex-1 truncate text-slate-700">
                      {rnc.description}
                    </span>
                    {atrasadas.length > 0 && <Badge tone="danger">prazo vencido</Badge>}
                    {rnc.status === 'CLOSED' && <Badge tone="success">encerrada</Badge>}
                  </li>
                );
              })}
          </ul>
        </Bloco>

        <Bloco
          titulo="Certificações"
          icone={<BadgeCheck className="size-4" aria-hidden />}
          vazio="Nenhum certificado enviado. Sem certificação vigente o ISQ fica em 0."
          acao={{ to: '/certificados', texto: 'Enviar' }}
          itens={data.certifications.length}
        >
          <ul className="divide-y divide-slate-100">
            {data.certifications.map((cert) => (
              <li key={cert.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="font-medium text-slate-800">
                  {CERT_STANDARD_LABEL[cert.standard]}
                </span>
                <span className="ml-auto text-slate-600">
                  {cert.daysUntilExpiry < 0 ? 'venceu em ' : 'até '}
                  {formatDate(cert.validUntil)}
                </span>
                {cert.reviewStatus === 'PENDING' && <Badge tone="warning">aguardando validação</Badge>}
                {cert.daysUntilExpiry < 0 && cert.reviewStatus === 'APPROVED' && (
                  <Badge tone="danger">vencido</Badge>
                )}
              </li>
            ))}
          </ul>
        </Bloco>

        <Bloco
          titulo="Documentos da ficha"
          icone={<FileText className="size-4" aria-hidden />}
          vazio="Nenhum documento cadastrado pela Qualidade."
          itens={data.documents.length}
        >
          <ul className="divide-y divide-slate-100">
            {data.documents.map((doc) => (
              <li key={doc.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="text-slate-800">
                  {SUPPLIER_DOC_KIND_LABEL[doc.kind]}
                  {doc.reference && <span className="ml-1 text-slate-500">{doc.reference}</span>}
                </span>
                <span className="ml-auto text-slate-600">
                  {doc.validUntil ? `até ${formatDate(doc.validUntil)}` : 'sem prazo'}
                </span>
                {doc.fileId && (
                  <a
                    href={`/api/files/${doc.fileId}`}
                    className="text-brand-700 underline-offset-4 hover:underline"
                  >
                    baixar
                  </a>
                )}
              </li>
            ))}
          </ul>
        </Bloco>

        <Bloco
          titulo="Planos de ação"
          icone={<ClipboardList className="size-4" aria-hidden />}
          vazio="Nenhum plano de ação aberto no seu nome."
          itens={data.actionPlans.length}
        >
          <p className="mb-2 text-sm text-slate-600">
            {planosAbertos.length === 0
              ? 'Nenhum em aberto.'
              : `${planosAbertos.length} em aberto.`}
          </p>
          <ul className="divide-y divide-slate-100">
            {data.actionPlans.slice(0, 6).map((plano) => (
              <li key={plano.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-700">{plano.description}</span>
                {plano.dueAt && (
                  <span className="text-xs text-slate-500">prazo {formatDate(plano.dueAt)}</span>
                )}
                <Badge
                  tone={
                    plano.status === 'DONE'
                      ? 'success'
                      : plano.status === 'OVERDUE'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {ACTION_PLAN_STATUS_LABEL[plano.status]}
                </Badge>
              </li>
            ))}
          </ul>
        </Bloco>

        <Bloco
          titulo="Auditorias"
          icone={<CalendarClock className="size-4" aria-hidden />}
          vazio="Nenhuma auditoria registrada."
          itens={data.audits.length}
        >
          <ul className="divide-y divide-slate-100">
            {data.audits.slice(0, 6).map((auditoria) => (
              <li key={auditoria.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
                <span className="text-slate-800">{AUDIT_KIND_LABEL[auditoria.kind]}</span>
                <span className="ml-auto text-slate-600">
                  {auditoria.performedAt
                    ? formatDate(auditoria.performedAt)
                    : auditoria.plannedFor
                      ? `prevista para ${formatDate(auditoria.plannedFor)}`
                      : '—'}
                </span>
                <Badge tone="neutral">{AUDIT_STATUS_LABEL[auditoria.status]}</Badge>
              </li>
            ))}
          </ul>
        </Bloco>

        <Bloco
          titulo="Contatos cadastrados"
          icone={<Mail className="size-4" aria-hidden />}
          vazio="Nenhum contato cadastrado — a Qualidade não tem para onde escrever."
          itens={data.contacts.length}
        >
          {/* Read-only de propósito: quem escreve aqui é a Qualidade, e um
              contato editado dos dois lados vira dois endereços diferentes
              recebendo metade dos avisos. */}
          <ul className="divide-y divide-slate-100">
            {data.contacts.map((contato) => (
              <li key={contato.id} className="py-2 text-sm">
                <span className="text-slate-800">{contato.name}</span>
                {contato.role && <span className="ml-2 text-slate-500">{contato.role}</span>}
                <p className="text-xs text-slate-500">{contato.email}</p>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-500">
            Para corrigir, fale com a Qualidade da Weloze.
          </p>
        </Bloco>
      </div>
    </div>
  );
}

function Pendencies({ pendencies }: { pendencies: PendencyDto[] }) {
  if (pendencies.length === 0) {
    return (
      <Card>
        <p className="text-sm text-emerald-700">Nenhuma pendência na sua ficha.</p>
      </Card>
    );
  }

  const criticas = pendencies.filter((p) => p.severity === 'CRITICAL').length;

  return (
    <Card className={criticas > 0 ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'}>
      <h2 className="flex items-center gap-2 font-medium text-slate-900">
        <AlertTriangle className="size-4 shrink-0 text-amber-600" aria-hidden />
        {pendencies.length === 1 ? '1 pendência' : `${pendencies.length} pendências`}
        {criticas > 0 && ` · ${criticas} crítica${criticas === 1 ? '' : 's'}`}
      </h2>
      <ul className="mt-2 space-y-1 text-sm">
        {pendencies.map((pendencia, index) => (
          <li key={`${pendencia.kind}-${index}`} className="flex gap-2">
            <span
              aria-hidden
              className={
                pendencia.severity === 'CRITICAL'
                  ? 'mt-2 size-1.5 shrink-0 rounded-full bg-red-500'
                  : 'mt-2 size-1.5 shrink-0 rounded-full bg-amber-500'
              }
            />
            <span className="text-slate-700">{pendencia.label}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Bloco({
  titulo,
  icone,
  vazio,
  acao,
  itens,
  children,
}: {
  titulo: string;
  icone: React.ReactNode;
  vazio: string;
  acao?: { to: string; texto: string };
  itens: number;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 font-medium text-slate-900">
          <span className="text-slate-400">{icone}</span>
          {titulo}
        </h2>
        {acao && (
          <Link to={acao.to} className="text-sm text-brand-700 underline-offset-4 hover:underline">
            {acao.texto}
          </Link>
        )}
      </div>
      {itens === 0 ? <p className="text-sm text-slate-600">{vazio}</p> : children}
    </Card>
  );
}
